import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface Content {
  id: string;
  title: string;
  views: number;
  created_at: string;
  description?: string;
  thumbnail_url?: string;
  content_url?: string;
  file_urls?: string[];
}

interface Order {
  id: string;
  created_at: string;
  total_amount: number;
  status: string;
  order_items: {
    id: string;
    product_id: string;
    product_name: string;
    quantity: number;
    price: number;
  }[];
}

interface Transaction {
  id: string;
  amount: number;
  type: string;
  description: string;
  created_at: string;
}

interface CommunityPost {
  id: string;
  title: string;
  content: string;
  created_at: string;
  likes: number;
}

export default function MyPage() {
  const navigate = useNavigate();
  const { user, profile, loading, signOut, isAdminAsMember, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [pageLoading, setPageLoading] = useState(false);

  // 통합관리자 체크
  const [isAdmin, setIsAdmin] = useState(false);

  // 데이터 상태
  const [talentBalance, setTalentBalance] = useState(0);
  const [myContents, setMyContents] = useState<Content[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [communityPosts, setCommunityPosts] = useState<CommunityPost[]>([]);
  const [stats, setStats] = useState({
    contentCount: 0,
    orderCount: 0,
    postCount: 0,
    monthlyEarned: 0,
    monthlySpent: 0
  });

  // 페이징 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [totalContents, setTotalContents] = useState(0);
  const contentsPerPage = 10;

  // 설정 폼 상태
  const [formData, setFormData] = useState({
    full_name: '',
    church_name: '',
    phone: '',
    address: ''
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // 주문 상세 모달 상태
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // 교환 신청 모달 상태
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [exchangeReason, setExchangeReason] = useState('');
  const [exchangeImages, setExchangeImages] = useState<File[]>([]);
  const [exchangeImagePreviews, setExchangeImagePreviews] = useState<string[]>([]);
  const [exchangeLoading, setExchangeLoading] = useState(false);

  // 통합관리자 체크 및 초기 로딩
  useEffect(() => {
    const adminSession = sessionStorage.getItem('admin_session');
    const adminUsername = sessionStorage.getItem('admin_username');
    
    if (adminSession === 'true' && adminUsername === 'wonderm') {
      setIsAdmin(true);
      return;
    }

    if (!loading && !user && !isAdminAsMember) {
      navigate('/auth/login', { state: { from: { pathname: '/mypage' } } });
    }
  }, [user, loading, navigate, isAdminAsMember]);

  // 프로필 데이터를 폼에 동기화
  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        church_name: profile.church_name || '',
        phone: profile.phone || '',
        address: profile.address || ''
      });
    }
  }, [profile]);

  // 사용자 데이터 로드
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user || isAdmin) return;

      setPageLoading(true);
      try {
        // 콘텐츠 조회
        const { data: contentsData, error: contentsError } = await supabase
          .from('contents')
          .select('id, title, views, created_at, description, thumbnail_url, content_url, file_urls')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);

        if (contentsError) throw contentsError;
        setMyContents(contentsData || []);

        // 커뮤니티 게시글 조회
        const { data: postsData, error: postsError } = await supabase
          .from('community_posts')
          .select('id, title, content, created_at, likes')
          .eq('author_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);

        if (postsError && postsError.code !== 'PGRST116') throw postsError;
        setCommunityPosts(postsData || []);

        // 탤런트 포인트 조회
        const { data: pointsData, error: pointsError } = await supabase
          .from('talent_points')
          .select('balance')
          .eq('user_id', user.id)
          .single();

        if (pointsError && pointsError.code !== 'PGRST116') throw pointsError;
        setTalentBalance(pointsData?.balance || 0);

        // 주문 조회
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select(`
            id,
            created_at,
            total_amount,
            status,
            order_items (
              id,
              product_id,
              product_name,
              quantity,
              price
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);

        if (ordersError && ordersError.code !== 'PGRST116') throw ordersError;
        setOrders(ordersData as Order[] || []);

        // 거래 내역 조회
        const { data: transactionsData, error: transactionsError } = await supabase
          .from('point_transactions')
          .select('id, amount, type, description, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(20);

        if (transactionsError && transactionsError.code !== 'PGRST116') throw transactionsError;
        setTransactions(transactionsData || []);

        // 통계 로드
        await loadStats();
      } catch (error) {
        console.error('데이터 로딩 오류:', error);
      } finally {
        setPageLoading(false);
      }
    };

    fetchUserData();
  }, [user, isAdmin]);

  // 콘텐츠 페이지 변경 시 다시 로드
  useEffect(() => {
    if (user && activeTab === 'content' && !isAdmin) {
      loadContents();
    }
  }, [currentPage, user, activeTab, isAdmin]);

  const loadContents = async () => {
    if (!user) return;
    
    // 전체 콘텐츠 수 조회
    const { count } = await supabase
      .from('contents')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id);

    setTotalContents(count || 0);

    // 페이지네이션 적용하여 콘텐츠 조회
    const from = (currentPage - 1) * contentsPerPage;
    const to = from + contentsPerPage - 1;

    const { data, error } = await supabase
      .from('contents')
      .select('id, title, views, created_at, description, thumbnail_url, content_url, file_urls')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (!error && data) {
      setMyContents(data);
    }
  };

  const loadStats = async () => {
    if (!user) return;
    
    // 콘텐츠 수
    const { count: contentCount } = await supabase
      .from('contents')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id);

    // 주문 수
    const { count: orderCount } = await supabase
      .from('orders')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id);

    // 커뮤니티 게시글 수
    const { count: postCount } = await supabase
      .from('community_posts')
      .select('*', { count: 'exact', head: true })
      .eq('author_id', user.id);

    // 이번 달 적립/사용
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const { data: monthlyTransactions } = await supabase
      .from('point_transactions')
      .select('amount')
      .eq('user_id', user.id)
      .gte('created_at', startOfMonth.toISOString());

    let monthlyEarned = 0;
    let monthlySpent = 0;

    if (monthlyTransactions) {
      monthlyTransactions.forEach(t => {
        if (t.amount > 0) {
          monthlyEarned += t.amount;
        } else {
          monthlySpent += Math.abs(t.amount);
        }
      });
    }

    setStats({
      contentCount: contentCount || 0,
      orderCount: orderCount || 0,
      postCount: postCount || 0,
      monthlyEarned,
      monthlySpent
    });
  };

  const handleContentClick = (contentId: string) => {
    navigate(`/content?id=${contentId}`);
  };

  const handleCommunityPostClick = (postId: string) => {
    navigate(`/community?post=${postId}`);
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setSaveLoading(true);
    setSaveMessage('');

    try {
      await updateProfile({
        full_name: formData.full_name,
        church_name: formData.church_name,
        phone: formData.phone,
        address: formData.address
      });
      setSaveMessage('프로필이 저장되었습니다');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      setSaveMessage(error.message || '저장에 실패했습니다');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // 통합관리자 로그아웃
      if (isAdmin) {
        sessionStorage.removeItem('admin_session');
        sessionStorage.removeItem('admin_username');
        navigate('/');
        return;
      }

      await signOut();
      navigate('/');
    } catch (error: any) {
      alert(error.message || '로그아웃에 실패했습니다');
    }
  };

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderDetailModal(true);
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder || !user) return;

    if (selectedOrder.status === 'cancelled') {
      alert('이미 취소된 주문입니다');
      return;
    }

    if (selectedOrder.status === 'delivered') {
      alert('배송 완료된 주문은 취소할 수 없습니다');
      return;
    }

    if (!confirm('주문을 취소하시겠습니까?')) return;

    setActionLoading(true);
    try {
      // 주문 상태 업데이트
      const { error: orderError } = await supabase
        .from('orders')
        .update({ status: 'cancelled' })
        .eq('id', selectedOrder.id);

      if (orderError) throw orderError;

      // 재고 복구
      for (const item of selectedOrder.order_items) {
        const { data: productData } = await supabase
          .from('products')
          .select('stock')
          .eq('id', item.product_id)
          .single();

        if (productData) {
          await supabase
            .from('products')
            .update({ stock: productData.stock + item.quantity })
            .eq('id', item.product_id);
        }
      }

      alert('주문이 취소되었습니다');
      setShowOrderDetailModal(false);
      
      // 주문 목록 다시 로드
      const { data: ordersData } = await supabase
        .from('orders')
        .select(`
          id,
          created_at,
          total_amount,
          status,
          order_items (
            id,
            product_id,
            product_name,
            quantity,
            price
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (ordersData) {
        setOrders(ordersData as Order[]);
      }
    } catch (error) {
      console.error('주문 취소 실패:', error);
      alert('주문 취소 중 오류가 발생했습니다');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestExchange = async () => {
    if (!selectedOrder) return;

    if (selectedOrder.status !== 'delivered') {
      alert('배송 완료된 주문만 교환 신청이 가능합니다');
      return;
    }

    // 교환 모달 열기
    setShowExchangeModal(true);
  };

  const handleExchangeImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const fileArray = Array.from(files);
    
    // 최대 5개까지만 허용
    if (exchangeImages.length + fileArray.length > 5) {
      alert('최대 5개의 이미지만 업로드할 수 있습니다');
      return;
    }

    setExchangeImages(prev => [...prev, ...fileArray]);

    // 미리보기 생성
    fileArray.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setExchangeImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeExchangeImage = (index: number) => {
    setExchangeImages(prev => prev.filter((_, i) => i !== index));
    setExchangeImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const submitExchangeRequest = async () => {
    if (!selectedOrder || !user) return;

    if (!exchangeReason.trim()) {
      alert('교환 사유를 입력해주세요');
      return;
    }

    if (exchangeImages.length === 0) {
      alert('상품 사진을 최소 1장 이상 업로드해주세요');
      return;
    }

    setExchangeLoading(true);
    try {
      // 이미지 업로드
      const uploadedUrls: string[] = [];
      
      for (const image of exchangeImages) {
        const fileExt = image.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('exchange-images')
          .upload(fileName, image);

        if (uploadError) {
          console.error('이미지 업로드 오류:', uploadError);
          throw new Error('이미지 업로드에 실패했습니다');
        }

        const { data: { publicUrl } } = supabase.storage
          .from('exchange-images')
          .getPublicUrl(fileName);

        uploadedUrls.push(publicUrl);
      }

      // 주문 상태 업데이트 및 교환 정보 저장
      const { error: updateError } = await supabase
        .from('orders')
        .update({ 
          status: 'exchange_requested',
          exchange_reason: exchangeReason,
          exchange_images: uploadedUrls
        })
        .eq('id', selectedOrder.id);

      if (updateError) throw updateError;

      alert('교환 신청이 완료되었습니다. 판매자가 확인 후 연락드립니다.');
      
      // 모달 닫기 및 초기화
      setShowExchangeModal(false);
      setShowOrderDetailModal(false);
      setExchangeReason('');
      setExchangeImages([]);
      setExchangeImagePreviews([]);
      
      // 주문 목록 다시 로드
      const { data: ordersData } = await supabase
        .from('orders')
        .select(`
          id,
          created_at,
          total_amount,
          status,
          order_items (
            id,
            product_id,
            product_name,
            quantity,
            price
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (ordersData) {
        setOrders(ordersData as Order[]);
      }
    } catch (error) {
      console.error('교환 신청 실패:', error);
      alert('교환 신청 중 오류가 발생했습니다');
    } finally {
      setExchangeLoading(false);
    }
  };

  const handleRequestReturn = async () => {
    if (!selectedOrder || !user) return;

    if (selectedOrder.status !== 'delivered') {
      alert('배송 완료된 주문만 반품 신청이 가능합니다');
      return;
    }

    if (!confirm('반품을 신청하시겠습니까?')) return;

    setActionLoading(true);
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'return_requested' })
        .eq('id', selectedOrder.id);

      if (error) throw error;

      alert('반품 신청이 완료되었습니다. 판매자가 확인 후 연락드립니다.');
      setShowOrderDetailModal(false);
      
      // 주문 목록 다시 로드
      const { data: ordersData } = await supabase
        .from('orders')
        .select(`
          id,
          created_at,
          total_amount,
          status,
          order_items (
            id,
            product_id,
            product_name,
            quantity,
            price
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (ordersData) {
        setOrders(ordersData as Order[]);
      }
    } catch (error) {
      console.error('반품 신청 실패:', error);
      alert('반품 신청 중 오류가 발생했습니다');
    } finally {
      setActionLoading(false);
    }
  };

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      earn: '적립',
      spend: '사용',
      exchange: '교환',
      reward: '보상'
    };
    return labels[type] || type;
  };

  const getOrderStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: '결제대기',
      paid: '결제완료',
      processing: '처리중',
      shipped: '배송중',
      delivered: '배송완료',
      cancelled: '취소됨',
      exchange_requested: '교환신청',
      return_requested: '반품신청'
    };
    return labels[status] || status;
  };

  const getOrderStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-700',
      paid: 'bg-blue-100 text-blue-700',
      processing: 'bg-purple-100 text-purple-700',
      shipped: 'bg-indigo-100 text-indigo-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      exchange_requested: 'bg-orange-100 text-orange-700',
      return_requested: 'bg-pink-100 text-pink-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getContentThumbnail = (content: Content) => {
    // 1. 첨부된 이미지 파일 우선
    if (content.file_urls && content.file_urls.length > 0) {
      const imageFile = content.file_urls.find(url => 
        url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)
      );
      if (imageFile) return imageFile;
    }

    // 2. thumbnail_url
    if (content.thumbnail_url) return content.thumbnail_url;

    // 3. content_url에서 썸네일 추출
    if (content.content_url) {
      const url = content.content_url.toLowerCase();
      
      // YouTube
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        let videoId = '';
        if (url.includes('youtube.com/watch?v=')) {
          videoId = content.content_url.split('v=')[1]?.split('&')[0];
        } else if (url.includes('youtu.be/')) {
          videoId = content.content_url.split('youtu.be/')[1]?.split('?')[0];
        }
        if (videoId) {
          return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        }
      }
      
      // 이미지 URL
      if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
        return content.content_url;
      }
    }

    // 4. AI 생성 썸네일 (제목과 설명 기반)
    const query = `${content.title} ${content.description || ''} christian faith spiritual content simple background peaceful atmosphere soft lighting minimalist design religious theme inspirational message clean aesthetic modern style warm tones serene mood`.substring(0, 200);
    return `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28query%29%7D&width=400&height=300&seq=content-${content.id}&orientation=landscape`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getYouTubeVideoId = (url: string) => {
    if (!url) return null;
    
    const urlLower = url.toLowerCase();
    let videoId = '';
    
    if (urlLower.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (urlLower.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    
    return videoId || null;
  };

  // 페이지네이션 렌더링 함수
  const renderPagination = () => {
    const totalPages = Math.ceil(totalContents / contentsPerPage);
    
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center justify-center space-x-2 mt-6">
        <button
          onClick={() => {
            setCurrentPage(prev => Math.max(1, prev - 1));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          disabled={currentPage === 1}
          className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        
        {startPage > 1 && (
          <>
            <button
              onClick={() => {
                setCurrentPage(1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              1
            </button>
            {startPage > 2 && (
              <span className="text-gray-400">...</span>
            )}
          </>
        )}
        
        {pageNumbers.map(number => (
          <button
            key={number}
            onClick={() => {
              setCurrentPage(number);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`w-10 h-10 flex items-center justify-center rounded-lg font-medium transition-colors cursor-pointer ${
              currentPage === number
                ? 'bg-teal-600 text-white'
                : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {number}
          </button>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <span className="text-gray-400">...</span>
            )}
            <button
              onClick={() => {
                setCurrentPage(totalPages);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              {totalPages}
            </button>
          </>
        )}
        
        <button
          onClick={() => {
            setCurrentPage(prev => Math.min(totalPages, prev + 1));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          disabled={currentPage === totalPages}
          className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: '개요', icon: 'ri-dashboard-line' },
    { id: 'talent', label: '달란트', icon: 'ri-coin-line' },
    { id: 'content', label: '내 콘텐츠', icon: 'ri-article-line' },
    { id: 'community', label: '커뮤니티', icon: 'ri-chat-3-line' },
    { id: 'orders', label: '주문내역', icon: 'ri-shopping-bag-line' },
    { id: 'settings', label: '설정', icon: 'ri-settings-3-line' }
  ];

  if (loading || pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!user && !isAdminAsMember && !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">마이페이지</h1>
          <p className="text-gray-600">회원님의 활동과 정보를 관리하세요</p>
        </div>

        {/* 탭 메뉴 */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex space-x-2 overflow-x-auto">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-all ${
                activeTab === 'profile'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className="ri-user-line mr-2"></i>
              <span className="font-medium">프로필</span>
            </button>
            <button
              onClick={() => setActiveTab('talent')}
              className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-all ${
                activeTab === 'talent'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className="ri-coin-line mr-2"></i>
              <span className="font-medium">달란트</span>
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-all ${
                activeTab === 'content'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className="ri-article-line mr-2"></i>
              <span className="font-medium">내 콘텐츠</span>
            </button>
            <button
              onClick={() => setActiveTab('community')}
              className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-all ${
                activeTab === 'community'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className="ri-chat-3-line mr-2"></i>
              <span className="font-medium">커뮤니티</span>
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-all ${
                activeTab === 'orders'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className="ri-shopping-bag-line mr-2"></i>
              <span className="font-medium">주문내역</span>
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-all ${
                activeTab === 'settings'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className="ri-settings-3-line mr-2"></i>
              <span className="font-medium">설정</span>
            </button>
          </div>
        </div>

        {/* 탭 콘텐츠 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-line text-4xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{profile.full_name}</h3>
                <p className="text-sm text-gray-600">{profile.church_name || '교회 미등록'}</p>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">보유 달란트</p>
                <div className="flex items-center space-x-2">
                  <i className="ri-coin-line text-2xl text-amber-600"></i>
                  <p className="text-3xl font-bold text-gray-900">{talentBalance.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`w-full px-6 py-4 flex items-center space-x-3 transition-colors cursor-pointer ${
                    activeTab === tab.id
                      ? 'bg-teal-50 text-teal-600 border-r-4 border-teal-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${tab.icon} text-xl`}></i>
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </div>

            <button
              onClick={handleLogout}
              className="w-full px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line mr-2"></i>
              로그아웃
            </button>
          </div>

          <div className="lg:col-span-2">
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                        <i className="ri-book-open-line text-2xl text-teal-600"></i>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">업로드 콘텐츠</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.contentCount}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                        <i className="ri-shopping-bag-line text-2xl text-amber-600"></i>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">구매 내역</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.orderCount}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <i className="ri-article-line text-2xl text-green-600"></i>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">커뮤니티 게시글</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.postCount}</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                        <i className="ri-book-open-line text-2xl text-teal-600"></i>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">업로드 콘텐츠</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.contentCount}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                        <i className="ri-shopping-bag-line text-2xl text-amber-600"></i>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">구매 내역</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.orderCount}</p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <i className="ri-article-line text-2xl text-green-600"></i>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">커뮤니티 게시글</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.postCount}</p>
                  </div>
                </div>

                {/* 최근 활동 */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">최근 활동</h3>
                  <div className="space-y-4">
                    {/* 최근 콘텐츠 */}
                    {myContents.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">최근 업로드한 콘텐츠</h4>
                        <div className="space-y-2">
                          {myContents.slice(0, 3).map((content) => (
                            <div 
                              key={content.id}
                              onClick={() => handleContentClick(content.id)}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="relative w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                                  <img
                                    src={getContentThumbnail(content)}
                                    alt={content.title}
                                    className="w-full h-full object-cover object-top"
                                  />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{content.title}</p>
                                  <p className="text-sm text-gray-600">조회수: {content.views?.toLocaleString() || 0}</p>
                                </div>
                              </div>
                              <i className="ri-arrow-right-s-line text-xl text-gray-400"></i>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 최근 주문 */}
                    {orders.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">최근 주문</h4>
                        <div className="space-y-2">
                          {orders.slice(0, 3).map((order) => (
                            <div 
                              key={order.id}
                              onClick={() => handleOrderClick(order)}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                            >
                              <div>
                                <p className="font-medium text-gray-900">
                                  {order.order_items?.[0]?.product_name || '주문 상품'}
                                  {order.order_items && order.order_items.length > 1 && ` 외 ${order.order_items.length - 1}건`}
                                </p>
                                <p className="text-sm text-gray-600">{formatDate(order.created_at)}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-gray-900">{order.total_amount.toLocaleString()}원</p>
                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getOrderStatusColor(order.status)}`}>
                                  {getOrderStatusLabel(order.status)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 최근 커뮤니티 활동 */}
                    {communityPosts.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">최근 커뮤니티 활동</h4>
                        <div className="space-y-2">
                          {communityPosts.slice(0, 3).map((post) => (
                            <div 
                              key={post.id}
                              onClick={() => handleCommunityPostClick(post.id)}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                            >
                              <div>
                                <p className="font-medium text-gray-900">{post.title}</p>
                                <p className="text-sm text-gray-600">{formatDate(post.created_at)}</p>
                              </div>
                              <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <i className="ri-heart-line text-red-500"></i>
                                <span>{post.likes}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 활동이 없을 때 */}
                    {myContents.length === 0 && orders.length === 0 && communityPosts.length === 0 && (
                      <div className="text-center py-12">
                        <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
                        <p className="text-gray-600 mb-4">아직 활동 내역이 없습니다</p>
                        <div className="flex justify-center space-x-3">
                          <button 
                            onClick={() => navigate('/content')}
                            className="px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            콘텐츠 업로드
                          </button>
                          <button 
                            onClick={() => navigate('/commerce')}
                            className="px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            상품 둘러보기
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'talent' && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-lg p-8 text-white">
                  <p className="text-lg mb-2">보유 달란트</p>
                  <div className="flex items-center space-x-3 mb-6">
                    <i className="ri-coin-line text-5xl"></i>
                    <p className="text-5xl font-bold">{talentBalance.toLocaleString()}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/20 rounded-lg p-4">
                      <p className="text-sm text-amber-100 mb-1">이번 달 적립</p>
                      <p className="text-2xl font-bold">+{stats.monthlyEarned.toLocaleString()}</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-4">
                      <p className="text-sm text-amber-100 mb-1">이번 달 사용</p>
                      <p className="text-2xl font-bold">-{stats.monthlySpent.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">달란트 내역</h3>
                  {transactions.length === 0 ? (
                    <div className="text-center py-12">
                      <i className="ri-coin-line text-6xl text-gray-300 mb-4"></i>
                      <p className="text-gray-600 mb-4">달란트 내역이 없습니다</p>
                      <button 
                        onClick={() => navigate('/content')}
                        className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        첫 콘텐츠 업로드하기
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transactions.map((transaction) => (
                        <div key={transaction.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-semibold text-gray-900">{getTransactionTypeLabel(transaction.type)}</p>
                            <p className="text-sm text-gray-600">{transaction.description}</p>
                            <p className="text-xs text-gray-500">{formatDate(transaction.created_at)}</p>
                          </div>
                          <p className={`text-xl font-bold ${transaction.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'content' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">내 콘텐츠</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      전체 {totalContents}개 중 {((currentPage - 1) * contentsPerPage) + 1}-{Math.min(currentPage * contentsPerPage, totalContents)}개 표시
                    </p>
                  </div>
                  <button 
                    onClick={() => navigate('/content')}
                    className="px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line mr-2"></i>
                    새 콘텐츠
                  </button>
                </div>
                {myContents.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-book-open-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-600 mb-4">업로드한 콘텐츠가 없습니다</p>
                    <button 
                      onClick={() => navigate('/content')}
                      className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      첫 콘텐츠 업로드하기
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {myContents.map((content) => (
                        <div 
                          key={content.id} 
                          onClick={() => handleContentClick(content.id)}
                          className="border border-gray-200 rounded-lg hover:shadow-md hover:border-teal-300 transition-all cursor-pointer overflow-hidden"
                        >
                          <div className="relative w-full h-40 bg-gray-100 overflow-hidden">
                            <img
                              src={getContentThumbnail(content)}
                              alt={content.title}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-4">
                            <h4 className="font-semibold text-gray-900 mb-2 line-clamp-1">{content.title}</h4>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <div className="flex items-center space-x-1">
                                <i className="ri-eye-line"></i>
                                <span>{content.views?.toLocaleString() || 0}</span>
                              </div>
                              {getYouTubeVideoId(content.content_url || '') && (
                                <div className="flex items-center space-x-1 text-red-600">
                                  <i className="ri-play-circle-fill"></i>
                                </div>
                              )}
                              {content.content_url && !getYouTubeVideoId(content.content_url) && (
                                <div className="flex items-center space-x-1 text-blue-600">
                                  <i className="ri-link"></i>
                                </div>
                              )}
                              <span>{formatDate(content.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* 페이지네이션 */}
                    {renderPagination()}
                  </>
                )}
              </div>
            )}

            {activeTab === 'community' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">내 커뮤니티 활동</h3>
                  <button 
                    onClick={() => navigate('/community')}
                    className="px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line mr-2"></i>
                    새 게시글
                  </button>
                </div>
                {communityPosts.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-team-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-600 mb-4">작성한 게시글이 없습니다</p>
                    <button 
                      onClick={() => navigate('/community')}
                      className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      첫 게시글 작성하기
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {communityPosts.map((post) => (
                      <div 
                        key={post.id} 
                        onClick={() => handleCommunityPostClick(post.id)}
                        className="p-4 border border-gray-200 rounded-lg hover:shadow-md hover:border-teal-300 transition-all cursor-pointer"
                      >
                        <h4 className="font-semibold text-gray-900 mb-2">{post.title}</h4>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{post.content}</p>
                        <div className="flex items-center justify-between text-sm text-gray-600">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                              <i className="ri-heart-line text-red-500"></i>
                              <span>{post.likes}</span>
                            </div>
                            <span>{formatDate(post.created_at)}</span>
                          </div>
                          <i className="ri-arrow-right-s-line text-xl text-gray-400"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'orders' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">주문 내역</h3>
                {orders.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-shopping-bag-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-600 mb-4">주문 내역이 없습니다</p>
                    <button 
                      onClick={() => navigate('/commerce')}
                      className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      상품 둘러보기
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div 
                        key={order.id} 
                        onClick={() => handleOrderClick(order)}
                        className="p-4 border border-gray-200 rounded-lg hover:shadow-md hover:border-teal-300 transition-all cursor-pointer"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm text-gray-600">주문번호: {order.id.slice(0, 8)}</span>
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getOrderStatusColor(order.status)}`}>
                            {getOrderStatusLabel(order.status)}
                          </span>
                        </div>
                        {order.order_items && order.order_items.length > 0 && (
                          <div className="mb-3">
                            {order.order_items.map((item, idx) => (
                              <p key={idx} className="text-gray-900 font-medium">
                                {item.product_name} x {item.quantity}
                              </p>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between text-sm text-gray-600">
                          <span>{formatDate(order.created_at)}</span>
                          <span className="text-lg font-bold text-gray-900">{order.total_amount.toLocaleString()}원</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">설정</h3>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">이름</label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">이메일</label>
                    <input
                      type="email"
                      value={profile.email}
                      disabled
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">이메일은 변경할 수 없습니다</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">소속 교회</label>
                    <input
                      type="text"
                      value={formData.church_name}
                      onChange={(e) => setFormData({ ...formData, church_name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">주소</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="서울특별시 강남구 테헤란로 123"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">전화번호</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="010-1234-5678"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>
                  {saveMessage && (
                    <div className={`p-4 rounded-lg ${
                      saveMessage.includes('성공') || saveMessage.includes('저장') 
                        ? 'bg-green-50 text-green-700' 
                        : 'bg-red-50 text-red-700'
                    }`}>
                      {saveMessage}
                    </div>
                  )}
                  <button 
                    onClick={handleSaveProfile}
                    disabled={saveLoading}
                    className="w-full px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saveLoading ? '저장 중...' : '저장하기'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 주문 상세 모달 */}
      {showOrderDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">주문 상세</h3>
              <button
                onClick={() => setShowOrderDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* 주문 정보 */}
            <div className="space-y-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 mb-1">주문번호</p>
                    <p className="font-semibold text-gray-900">{selectedOrder.id}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">주문일시</p>
                    <p className="font-semibold text-gray-900">{formatDate(selectedOrder.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">주문상태</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getOrderStatusColor(selectedOrder.status)}`}>
                      {getOrderStatusLabel(selectedOrder.status)}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">결제금액</p>
                    <p className="font-bold text-lg text-teal-600">{selectedOrder.total_amount.toLocaleString()}원</p>
                  </div>
                </div>
              </div>

              {/* 배송 현황 */}
              <div>
                <h4 className="font-bold text-gray-900 mb-4">배송 현황</h4>
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  <div className="space-y-4">
                    <div className={`relative pl-10 ${selectedOrder.status === 'pending' || selectedOrder.status === 'paid' || selectedOrder.status === 'processing' || selectedOrder.status === 'shipped' || selectedOrder.status === 'delivered' ? 'opacity-100' : 'opacity-50'}`}>
                      <div className={`absolute left-2 w-4 h-4 rounded-full ${selectedOrder.status === 'pending' || selectedOrder.status === 'paid' || selectedOrder.status === 'processing' || selectedOrder.status === 'shipped' || selectedOrder.status === 'delivered' ? 'bg-teal-600' : 'bg-gray-300'}`}></div>
                      <p className="font-semibold text-gray-900">주문 접수</p>
                      <p className="text-sm text-gray-600">주문이 접수되었습니다</p>
                    </div>
                    <div className={`relative pl-10 ${selectedOrder.status === 'processing' || selectedOrder.status === 'shipped' || selectedOrder.status === 'delivered' ? 'opacity-100' : 'opacity-50'}`}>
                      <div className={`absolute left-2 w-4 h-4 rounded-full ${selectedOrder.status === 'processing' || selectedOrder.status === 'shipped' || selectedOrder.status === 'delivered' ? 'bg-teal-600' : 'bg-gray-300'}`}></div>
                      <p className="font-semibold text-gray-900">상품 준비중</p>
                      <p className="text-sm text-gray-600">판매자가 상품을 준비하고 있습니다</p>
                    </div>
                    <div className={`relative pl-10 ${selectedOrder.status === 'shipped' || selectedOrder.status === 'delivered' ? 'opacity-100' : 'opacity-50'}`}>
                      <div className={`absolute left-2 w-4 h-4 rounded-full ${selectedOrder.status === 'shipped' || selectedOrder.status === 'delivered' ? 'bg-teal-600' : 'bg-gray-300'}`}></div>
                      <p className="font-semibold text-gray-900">배송중</p>
                      <p className="text-sm text-gray-600">상품이 배송중입니다</p>
                    </div>
                    <div className={`relative pl-10 ${selectedOrder.status === 'delivered' ? 'opacity-100' : 'opacity-50'}`}>
                      <div className={`absolute left-2 w-4 h-4 rounded-full ${selectedOrder.status === 'delivered' ? 'bg-teal-600' : 'bg-gray-300'}`}></div>
                      <p className="font-semibold text-gray-900">배송 완료</p>
                      <p className="text-sm text-gray-600">상품이 배송 완료되었습니다</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 주문 상품 */}
              <div>
                <h4 className="font-bold text-gray-900 mb-4">주문 상품</h4>
                <div className="space-y-3">
                  {selectedOrder.order_items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{item.product_name}</p>
                        <p className="text-sm text-gray-600">수량: {item.quantity}개</p>
                      </div>
                      <p className="font-bold text-gray-900">{(item.price * item.quantity).toLocaleString()}원</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* 액션 버튼 */}
              <div className="grid grid-cols-2 gap-3">
                {(selectedOrder.status === 'pending' || selectedOrder.status === 'paid' || selectedOrder.status === 'processing') && (
                  <button
                    onClick={handleCancelOrder}
                    disabled={actionLoading}
                    className="col-span-2 px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-close-circle-line mr-2"></i>
                    주문 취소
                  </button>
                )}
                {selectedOrder.status === 'delivered' && (
                  <>
                    <button
                      onClick={handleRequestExchange}
                      disabled={actionLoading}
                      className="px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      <i className="ri-arrow-left-right-line mr-2"></i>
                      교환 신청
                    </button>
                    <button
                      onClick={handleRequestReturn}
                      disabled={actionLoading}
                      className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      <i className="ri-arrow-go-back-line mr-2"></i>
                      반품 신청
                    </button>
                  </>
                )}
                {selectedOrder.status === 'shipped' && (
                  <div className="col-span-2 p-4 bg-blue-50 rounded-lg text-center">
                    <i className="ri-truck-line text-3xl text-blue-600 mb-2"></i>
                    <p className="text-sm text-blue-800">상품이 배송중입니다. 배송 완료 후 교환/반품 신청이 가능합니다.</p>
                  </div>
                )}
                {(selectedOrder.status === 'cancelled' || selectedOrder.status === 'exchange_requested' || selectedOrder.status === 'return_requested') && (
                  <div className="col-span-2 p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">
                      {selectedOrder.status === 'cancelled' && '취소된 주문입니다.'}
                      {selectedOrder.status === 'exchange_requested' && '교환 신청이 접수되었습니다. 판매자가 확인 후 연락드립니다.'}
                      {selectedOrder.status === 'return_requested' && '반품 신청이 접수되었습니다. 판매자가 확인 후 연락드립니다.'}
                    </p>
                  </div>
                )}
              </div>

              {/* 안내 사항 */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h5 className="font-semibold text-blue-900 mb-2">
                  <i className="ri-information-line mr-2"></i>
                  안내 사항
                </h5>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• 배송 완료 후 7일 이내 교환/반품 신청이 가능합니다</li>
                  <li>• 상품 하자가 있는 경우 무료로 교환/반품이 가능합니다</li>
                  <li>• 단순 변심의 경우 왕복 배송비가 발생할 수 있습니다</li>
                  <li>• 교환/반품 신청 후 판매자가 확인하여 연락드립니다</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 교환 신청 모달 */}
      {showExchangeModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">교환 신청</h3>
              <button
                onClick={() => {
                  setShowExchangeModal(false);
                  setExchangeReason('');
                  setExchangeImages([]);
                  setExchangeImagePreviews([]);
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-6">
              {/* 주문 정보 */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-3">주문 정보</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">주문번호</span>
                    <span className="font-medium">{selectedOrder.id.slice(0, 8)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">주문일시</span>
                    <span className="font-medium">{formatDate(selectedOrder.created_at)}</span>
                  </div>
                  {selectedOrder.order_items.map((item, idx) => (
                    <div key={idx} className="flex justify-between pt-2 border-t">
                      <span className="text-gray-900 font-medium">{item.product_name}</span>
                      <span className="text-gray-600">수량: {item.quantity}개</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 교환 사유 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  교환 사유 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={exchangeReason}
                  onChange={(e) => setExchangeReason(e.target.value)}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  placeholder="교환 사유를 상세히 입력해주세요 (최대 500자)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {exchangeReason.length}/500자
                </p>
              </div>

              {/* 상품 사진 업로드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  상품 사진 <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  교환 사유를 확인할 수 있는 상품 사진을 업로드해주세요 (최대 5장)
                </p>
                
                {/* 이미지 미리보기 */}
                {exchangeImagePreviews.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {exchangeImagePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <div className="relative w-full h-32 bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={preview}
                            alt={`교환 사진 ${index + 1}`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <button
                          onClick={() => removeExchangeImage(index)}
                          className="absolute top-2 right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* 업로드 버튼 */}
                {exchangeImages.length < 5 && (
                  <label className="block w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleExchangeImageChange}
                      className="hidden"
                    />
                    <div className="text-center">
                      <i className="ri-image-add-line text-4xl text-gray-400 mb-2"></i>
                      <p className="text-sm text-gray-600">
                        클릭하여 이미지 업로드
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {exchangeImages.length}/5장
                      </p>
                    </div>
                  </label>
                )}
              </div>

              {/* 안내 사항 */}
              <div className="p-4 bg-orange-50 rounded-lg">
                <h5 className="font-semibold text-orange-900 mb-2">
                  <i className="ri-information-line mr-2"></i>
                  교환 안내
                </h5>
                <ul className="text-sm text-orange-800 space-y-1">
                  <li>• 배송 완료 후 7일 이내 교환 신청이 가능합니다</li>
                  <li>• 상품 하자가 있는 경우 무료로 교환/반품이 가능합니다</li>
                  <li>• 단순 변심의 경우 왕복 배송비가 발생할 수 있습니다</li>
                  <li>• 교환 신청 후 판매자가 확인하여 연락드립니다</li>
                  <li>• 상품의 상태를 확인할 수 있는 사진을 첨부해주세요</li>
                </ul>
              </div>

              {/* 버튼 */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowExchangeModal(false);
                    setExchangeReason('');
                    setExchangeImages([]);
                    setExchangeImagePreviews([]);
                  }}
                  disabled={exchangeLoading}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  onClick={submitExchangeRequest}
                  disabled={exchangeLoading || !exchangeReason.trim() || exchangeImages.length === 0}
                  className="flex-1 px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  {exchangeLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      처리 중...
                    </>
                  ) : (
                    <>
                      <i className="ri-arrow-left-right-line mr-2"></i>
                      교환 신청
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
